'use client';

import {useEffect} from 'react';
import {ErrorSection} from '@/app/_components/shared/error-section';
import {useLocale} from '@/app/_contexts/locale-context';
import {errorLabels, type ErrorLabelLocale} from '@/app/_lib/error-labels.generated';

interface SlugErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

export default function SlugError({error}: SlugErrorProps) {
    const locale = useLocale();

    // Labels come from a frozen TYPO3 snapshot (error-labels.generated.ts), not
    // the live API or a client cache — so this page renders localized text even
    // when the backend is down. Locale is derived from the URL, never the API.
    // locale comes from the URL and can be anything — check membership for real
    // instead of a lying cast that types the lookup as always-defined.
    const globalLabels = Object.hasOwn(errorLabels, locale)
      ? errorLabels[locale as ErrorLabelLocale]
      : errorLabels.de;

    const tagline = globalLabels.apiErrorTagline || 'Error Loading Page';
    const title = globalLabels.apiErrorTitle || 'API loading Error';
    const description = globalLabels.apiErrorDescription || "This page couldn't be loaded from the CMS. It might be temporarily unavailable or you don't have access to this content.";
    const resetButtonLabel = globalLabels.resetButtonLabel || 'Try again';
    const defaultButtonLabel = globalLabels.homeButtonLabel || 'Go to homepage';
    const homeHref = locale !== 'de' ? `/${locale}` : '/';

    useEffect(() => {
        console.error('Page loading error:', error);
    }, [error]);

    return (
        <ErrorSection
            tagline={tagline}
            title={title}
            description={description}
            resetButtonLabel={resetButtonLabel}
            defaultButtonLabel={defaultButtonLabel}
            homeHref={homeHref}
            showResetButton={false}
            error={error}
        />
    );
}
